import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import JobCard from "@/components/JobCard";
import { RevealLines } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { sectorLabel, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";
import { publicApi as api } from "@/lib/api";

export default function Jobs() {
  const { t, lang } = useLanguage();
  usePageMeta({ ...PAGE_META[lang].jobs, lang });
  const j = t.jobs;
  const [jobs, setJobs] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter) params.sector = filter;
    api.get("/jobs", { params }).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/sectors").then((r) => setSectors(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="pt-32 pb-28 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{j.kicker}</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-8xl tracking-tight uppercase mt-4 mb-12">
          <RevealLines lines={j.title} />
        </h1>

        <div className="flex flex-wrap gap-2 sm:gap-3 mb-12 -mx-1 px-1 overflow-x-auto">
          <button onClick={() => setFilter("")} data-testid="filter-all" className={`shrink-0 px-4 sm:px-5 py-3 text-xs sm:text-sm uppercase tracking-wider border transition-colors ${filter === "" ? "bg-y5-yellow text-y5-ink border-y5-yellow" : "border-y5-line text-y5-muted hover:border-white/40"}`}>{j.allSectors}</button>
          {sectors.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`shrink-0 px-4 sm:px-5 py-3 text-xs sm:text-sm uppercase tracking-wider border transition-colors ${filter === s ? "bg-y5-yellow text-y5-ink border-y5-yellow" : "border-y5-line text-y5-muted hover:border-white/40"}`}>{sectorLabel(s, lang)}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-y5-muted">{j.loading}</p>
        ) : jobs.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-y5-line p-16 text-center">
            <p className="text-white text-xl font-display mb-2">{j.emptyTitle}</p>
            <p className="text-y5-muted">{j.emptyText}</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job, i) => <JobCard key={job.id} job={job} index={i} />)}
          </div>
        )}

        <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6 border border-y5-line p-6 md:p-10 bg-y5-surface">
          <div>
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight">{t.apply.kicker}</h3>
            <p className="text-y5-muted mt-2 max-w-md">{t.jobs.emptyText}</p>
          </div>
          <Link to="/candidature" data-testid="jobs-spontaneous-cta" className="inline-flex items-center gap-2 bg-y5-yellow text-y5-ink px-8 py-4 font-semibold hover:scale-[1.03] transition-transform shrink-0">
            {t.hero.ctaSecondary} <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}
